import { useAppDispatch, useAppSelector } from 'app/hooks';
import Modal from 'components/common/modal';
import QuoteRecipientFields from 'components/quote-page/quote-recipient-fields';
import {
  AirHandlerWithQuantityType,
  CondenserWithQuantityType,
  QuoteEmailInputType,
  QuoteEmailResultsType,
  SelectedSystemType
} from 'data/types';
import { emptyApi } from 'features/api/empty-api';
import { setProjectDetails } from 'features/project';
import { setSystemIsQuoted, updateSelectedSystem } from 'features/systems';
import { useQuoteRecipients } from 'hooks/use-quote-recipients';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import axios from 'utils/api';
import { buildQuoteKitDescription } from 'utils/compatibility-systems';
import { KEPT_EQUIPMENT_PRODUCT_ID } from 'utils/equipment-placeholder';
import { saveQuoteInfo } from 'utils/quote';
import { quoteAccessoryCategory } from 'utils/quote-accessory-categories';
import {
  buildQuoteRecipientInput,
  withRememberedQuoteRecipients
} from 'utils/quote-recipients';
import { notifyQuoteSendResult } from 'utils/quote-send-notification';
import { resolveQuoteSendStatus } from 'utils/quote-send-status';
import { errorModal } from 'utils/ui';

type EmailSingleQuoteModalProps = {
  system: SelectedSystemType;
  openState: [boolean, Dispatch<SetStateAction<boolean>>];
};

export default function EmailSingleQuoteModal({
  system,
  openState
}: EmailSingleQuoteModalProps) {
  const dispatch = useAppDispatch();
  const { projectDetails } = useAppSelector((state) => state.project);
  const [open, setOpen] = openState;
  const {
    recipients,
    draft: recipientDraft,
    error: recipientError,
    handleEmailChange,
    handlePhoneNumberChange,
    handleEmailInputChange,
    handlePhoneNumberInputChange,
    validate: validateRecipients
  } = useQuoteRecipients(open);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (open) {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [open]);

  const handleSendQuote = async () => {
    if (submittingRef.current) {
      return;
    }
    if (validateRecipients()) {
      submittingRef.current = true;
      setIsSubmitting(true);
      setOpen(false);

      dispatch(
        updateSelectedSystem({
          uniqueId: system.uniqueId,
          quoteStatus: 'sending'
        })
      );

      const airHandlerProducts: AirHandlerWithQuantityType[] = [];
      const condenserProducts: CondenserWithQuantityType[] = [];

      system.configuration.condensers.forEach((cond) => {
        if (
          condenserProducts.find(
            (p) => p.productId === cond.selectedProduct?.productId
          )
        ) {
          const productIndex = condenserProducts.findIndex(
            (p) => p.productId === cond.selectedProduct?.productId
          );
          condenserProducts[productIndex].quantity += 1;
        } else if (cond.selectedProduct) {
          condenserProducts.push({
            ...cond.selectedProduct,
            quantity: 1
          });
        }

        cond.zones.forEach((zone) => {
          if (
            airHandlerProducts.find(
              (p) => p.productId === zone.selectedProduct?.productId
            )
          ) {
            const productIndex = airHandlerProducts.findIndex(
              (p) => p.productId === zone.selectedProduct?.productId
            );
            airHandlerProducts[productIndex].quantity += 1;
          } else if (zone.selectedProduct) {
            airHandlerProducts.push({
              ...zone.selectedProduct,
              quantity: 1
            });
          }

          // Kept blower placeholders (sentinel productId 0, AHA-6796) never
          // become a kit line — they aren't purchasable products.
          if (
            zone.blowerProduct &&
            zone.blowerProduct.productId !== KEPT_EQUIPMENT_PRODUCT_ID
          ) {
            const blowerIdx = airHandlerProducts.findIndex(
              (p) => p.productId === zone.blowerProduct!.productId
            );
            if (blowerIdx >= 0) {
              airHandlerProducts[blowerIdx].quantity += 1;
            } else {
              airHandlerProducts.push({
                ...zone.blowerProduct,
                quantity: 1
              });
            }
          }
        });
      });

      // Kit description with the kept-pairing marker carried into the
      // quoted artifact (AHA-6681 sub-build 4).
      const description = buildQuoteKitDescription(system);

      const kits = {
        'Option 1': {
          uniqueId: system.uniqueId,
          description,
          products: [
            ...airHandlerProducts.map((p) => ({
              productID: p.productId.toString(),
              quantity: p.quantity.toString(),
              quoteCategory: 'Primary Equipment'
            })),
            ...condenserProducts.map((p) => ({
              productID: p.productId.toString(),
              quantity: p.quantity.toString(),
              quoteCategory: 'Primary Equipment'
            })),
            ...system.quotableAccessories
              .filter((acc) => acc.quantity > 0)
              .map((acc) => ({
                productID: acc.productId.toString(),
                quantity: acc.quantity.toString(),
                quoteCategory: quoteAccessoryCategory(acc.type)
              }))
          ]
        }
      };

      const quoteInput: QuoteEmailInputType = {
        ...buildQuoteRecipientInput(recipients),
        kbType: 'Mini-Split v2',
        projectId: projectDetails?.projectId,
        customerContactId: projectDetails?.customerContactId,
        customerName: projectDetails?.customerName,
        kits
      };

      axios
        .post<QuoteEmailResultsType>(
          `${process.env.REACT_APP_PARTNERNET_URL}/kitbuilder/emailresults`,
          quoteInput
        )
        .then(async (res) => {
          const quoteStatus = resolveQuoteSendStatus(res.data, recipients);
          await saveQuoteInfo(res.data, [system.uniqueId], quoteStatus);

          dispatch(
            emptyApi.util.invalidateTags(['QuoteData', 'QuotableSystems'])
          );

          notifyQuoteSendResult(res.data);

          dispatch(
            updateSelectedSystem({
              uniqueId: system.uniqueId,
              quoteStatus
            })
          );
          dispatch(
            setSystemIsQuoted({
              uniqueId: system.uniqueId,
              isQuoted: quoteStatus === 'sent'
            })
          );

          dispatch(
            setProjectDetails(
              withRememberedQuoteRecipients(projectDetails, recipients)
            )
          );
        })
        .catch(() => {
          errorModal('Failed to send quote');
          submittingRef.current = false;
          setIsSubmitting(false);

          dispatch(
            updateSelectedSystem({
              uniqueId: system.uniqueId,
              quoteStatus: 'unsent'
            })
          );
        });
    }
  };

  return (
    <Modal
      title='Send Quote to Customer'
      buttonText={isSubmitting ? 'Sending...' : 'Send Quote'}
      openState={openState}
      onSave={handleSendQuote}
      saveDisabled={isSubmitting}
      closeOnSave={false}
    >
      <QuoteRecipientFields
        emailOptions={projectDetails?.customerEmails ?? []}
        phoneNumberOptions={projectDetails?.customerPhoneNumbers ?? []}
        draft={recipientDraft}
        error={recipientError}
        onEmailChange={handleEmailChange}
        onPhoneNumberChange={handlePhoneNumberChange}
        onEmailInputChange={handleEmailInputChange}
        onPhoneNumberInputChange={handlePhoneNumberInputChange}
      />
    </Modal>
  );
}
